import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';

const ACCENT = '#f97316';

interface OverviewData {
  active_syncs: number;
  records_today: number;
  error_rate: number;
  avg_duration_ms: number;
  total_runs_today: number;
}

interface SyncHealth {
  id: string;
  name: string;
  status: 'green' | 'yellow' | 'red';
  profile_status: string;
  connector_id: string;
  direction: string;
  frequency: string;
  last_sync: string | null;
  next_sync: string | null;
  records_per_sync: number;
  error_rate: number;
  sync_count: number;
}

interface ThroughputBucket {
  records: number;
  errors: number;
  hour: string;
}

interface ErrorTrendBucket {
  errors: number;
  day: string;
}

interface ActiveSync {
  id: string;
  sync_profile_id: string;
  profile_name: string;
  status: string;
  started_at: string;
  records_synced: number;
  errors_count: number;
  trigger: string;
}

function relativeTime(iso: string | null): string {
  if (!iso) return 'Never';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 0) {
    const absDiff = Math.abs(diff);
    if (absDiff < 60000) return `in ${Math.round(absDiff / 1000)}s`;
    if (absDiff < 3600000) return `in ${Math.round(absDiff / 60000)}m`;
    if (absDiff < 86400000) return `in ${Math.round(absDiff / 3600000)}h`;
    return `in ${Math.round(absDiff / 86400000)}d`;
  }
  if (diff < 60000) return `${Math.round(diff / 1000)}s ago`;
  if (diff < 3600000) return `${Math.round(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.round(diff / 3600000)}h ago`;
  return `${Math.round(diff / 86400000)}d ago`;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

// ─── Stat Card ───

function StatCard({ label, value, color, subtitle }: { label: string; value: string | number; color: string; subtitle?: string }) {
  return (
    <div style={{
      flex: 1,
      minWidth: 180,
      backgroundColor: '#fff',
      borderRadius: 8,
      padding: '1.25rem',
      border: '1px solid #e5e7eb',
      borderTop: `3px solid ${color}`,
    }}>
      <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#111827', marginTop: '0.25rem' }}>{value}</div>
      {subtitle && <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.125rem' }}>{subtitle}</div>}
    </div>
  );
}

// ─── Throughput Bar Chart (SVG) ───

function ThroughputChart({ data }: { data: ThroughputBucket[] }) {
  const [tooltip, setTooltip] = useState<{ x: number; y: number; text: string } | null>(null);

  const width = 900;
  const height = 260;
  const padLeft = 50;
  const padRight = 20;
  const padTop = 20;
  const padBottom = 40;
  const chartW = width - padLeft - padRight;
  const chartH = height - padTop - padBottom;

  const maxVal = Math.max(...data.map(d => d.records + d.errors), 1);
  const barW = Math.max(chartW / data.length - 2, 4);

  return (
    <div style={{ backgroundColor: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', padding: '1rem' }}>
      <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827', marginBottom: '0.5rem' }}>Records Synced (Last 24h)</div>
      <svg width="100%" viewBox={`0 0 ${width} ${height}`} style={{ overflow: 'visible' }}>
        {/* Y axis gridlines */}
        {[0, 0.25, 0.5, 0.75, 1].map(pct => {
          const y = padTop + chartH * (1 - pct);
          const val = Math.round(maxVal * pct);
          return (
            <g key={pct}>
              <line x1={padLeft} y1={y} x2={width - padRight} y2={y} stroke="#f3f4f6" strokeWidth={1} />
              <text x={padLeft - 8} y={y + 4} textAnchor="end" fontSize={10} fill="#9ca3af">{val}</text>
            </g>
          );
        })}

        {/* Bars */}
        {data.map((d, i) => {
          const x = padLeft + (chartW / data.length) * i + 1;
          const successH = (d.records / maxVal) * chartH;
          const errorH = (d.errors / maxVal) * chartH;
          const totalH = successH + errorH;
          const hourLabel = d.hour.slice(11, 13) + ':00';

          return (
            <g key={i}>
              {/* Success bar */}
              <rect
                x={x}
                y={padTop + chartH - totalH}
                width={barW}
                height={successH}
                fill="#22c55e"
                rx={2}
                onMouseEnter={(e) => setTooltip({
                  x: e.clientX,
                  y: e.clientY,
                  text: `${hourLabel}: ${d.records} records, ${d.errors} errors`,
                })}
                onMouseLeave={() => setTooltip(null)}
                style={{ cursor: 'pointer' }}
              />
              {/* Error bar stacked on top */}
              {d.errors > 0 && (
                <rect
                  x={x}
                  y={padTop + chartH - totalH}
                  width={barW}
                  height={errorH}
                  fill="#ef4444"
                  rx={2}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={(e) => setTooltip({
                    x: e.clientX,
                    y: e.clientY,
                    text: `${hourLabel}: ${d.records} records, ${d.errors} errors`,
                  })}
                  onMouseLeave={() => setTooltip(null)}
                />
              )}
              {/* X label every 3 hours */}
              {i % 3 === 0 && (
                <text x={x + barW / 2} y={height - padBottom + 16} textAnchor="middle" fontSize={10} fill="#9ca3af">{hourLabel}</text>
              )}
            </g>
          );
        })}

        {/* Axes */}
        <line x1={padLeft} y1={padTop} x2={padLeft} y2={padTop + chartH} stroke="#d1d5db" strokeWidth={1} />
        <line x1={padLeft} y1={padTop + chartH} x2={width - padRight} y2={padTop + chartH} stroke="#d1d5db" strokeWidth={1} />
      </svg>

      {/* Tooltip */}
      {tooltip && (
        <div style={{
          position: 'fixed',
          left: tooltip.x + 12,
          top: tooltip.y - 30,
          backgroundColor: '#111827',
          color: '#fff',
          padding: '0.375rem 0.625rem',
          borderRadius: 6,
          fontSize: '0.75rem',
          pointerEvents: 'none',
          zIndex: 1000,
          whiteSpace: 'nowrap',
        }}>
          {tooltip.text}
        </div>
      )}

      {/* Legend */}
      <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', justifyContent: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', color: '#6b7280' }}>
          <div style={{ width: 10, height: 10, backgroundColor: '#22c55e', borderRadius: 2 }} /> Success
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', color: '#6b7280' }}>
          <div style={{ width: 10, height: 10, backgroundColor: '#ef4444', borderRadius: 2 }} /> Errors
        </div>
      </div>
    </div>
  );
}

// ─── Error Trend Line Chart (SVG) ───

function ErrorTrendChart({ data }: { data: ErrorTrendBucket[] }) {
  const width = 400;
  const height = 220;
  const padLeft = 40;
  const padRight = 20;
  const padTop = 20;
  const padBottom = 40;
  const chartW = width - padLeft - padRight;
  const chartH = height - padTop - padBottom;

  const maxErrors = Math.max(...data.map(d => d.errors), 1);

  const points = data.map((d, i) => {
    const x = padLeft + (chartW / Math.max(data.length - 1, 1)) * i;
    const y = padTop + chartH - (d.errors / maxErrors) * chartH;
    return { x, y, ...d };
  });

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');

  return (
    <div style={{ backgroundColor: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', padding: '1rem', flex: 1 }}>
      <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827', marginBottom: '0.5rem' }}>Error Trend (7 Days)</div>
      <svg width="100%" viewBox={`0 0 ${width} ${height}`}>
        {/* Y gridlines */}
        {[0, 0.25, 0.5, 0.75, 1].map(pct => {
          const y = padTop + chartH * (1 - pct);
          const val = Math.round(maxErrors * pct);
          return (
            <g key={pct}>
              <line x1={padLeft} y1={y} x2={width - padRight} y2={y} stroke="#f3f4f6" strokeWidth={1} />
              <text x={padLeft - 6} y={y + 4} textAnchor="end" fontSize={10} fill="#9ca3af">{val}</text>
            </g>
          );
        })}

        {/* Line */}
        <path d={linePath} fill="none" stroke="#ef4444" strokeWidth={2} strokeLinejoin="round" />

        {/* Data points */}
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r={4} fill="#ef4444" stroke="#fff" strokeWidth={2} />
            <text x={p.x} y={height - padBottom + 16} textAnchor="middle" fontSize={9} fill="#9ca3af">
              {p.day.slice(5)}
            </text>
          </g>
        ))}

        {/* Axes */}
        <line x1={padLeft} y1={padTop} x2={padLeft} y2={padTop + chartH} stroke="#d1d5db" strokeWidth={1} />
        <line x1={padLeft} y1={padTop + chartH} x2={width - padRight} y2={padTop + chartH} stroke="#d1d5db" strokeWidth={1} />
      </svg>
    </div>
  );
}

// ─── Sync Health Table ───

function SyncHealthTable({ syncs }: { syncs: SyncHealth[] }) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const statusColor: Record<string, string> = { green: '#22c55e', yellow: '#eab308', red: '#ef4444' };

  return (
    <div style={{ backgroundColor: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', padding: '1rem', flex: 2 }}>
      <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827', marginBottom: '0.75rem' }}>Sync Health</div>
      {syncs.length === 0 ? (
        <div style={{ color: '#9ca3af', fontSize: '0.875rem', padding: '2rem', textAlign: 'center' }}>No sync profiles configured</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              {['Name', 'Status', 'Last Sync', 'Next Sync', 'Rec/Sync', 'Error Rate'].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '0.5rem 0.375rem', color: '#6b7280', fontWeight: 600, fontSize: '0.75rem' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {syncs.map(s => (
              <React.Fragment key={s.id}>
                <tr
                  onClick={() => setExpandedId(expandedId === s.id ? null : s.id)}
                  style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f9fafb')}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <td style={{ padding: '0.5rem 0.375rem', fontWeight: 500 }}>{s.name}</td>
                  <td style={{ padding: '0.5rem 0.375rem' }}>
                    <span style={{
                      display: 'inline-block',
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: statusColor[s.status] || '#9ca3af',
                      marginRight: 6,
                    }} />
                    {s.status}
                  </td>
                  <td style={{ padding: '0.5rem 0.375rem', color: '#6b7280' }}>{relativeTime(s.last_sync)}</td>
                  <td style={{ padding: '0.5rem 0.375rem', color: '#6b7280' }}>{relativeTime(s.next_sync)}</td>
                  <td style={{ padding: '0.5rem 0.375rem' }}>{s.records_per_sync.toLocaleString()}</td>
                  <td style={{ padding: '0.5rem 0.375rem', color: s.error_rate > 15 ? '#ef4444' : s.error_rate > 5 ? '#eab308' : '#22c55e' }}>
                    {s.error_rate}%
                  </td>
                </tr>
                {expandedId === s.id && (
                  <tr>
                    <td colSpan={6} style={{ padding: '0.75rem', backgroundColor: '#f9fafb' }}>
                      <div style={{ display: 'flex', gap: '2rem', fontSize: '0.75rem', color: '#6b7280' }}>
                        <div><strong>Connector:</strong> {s.connector_id}</div>
                        <div><strong>Direction:</strong> {s.direction}</div>
                        <div><strong>Frequency:</strong> {s.frequency}</div>
                        <div><strong>Total Syncs:</strong> {s.sync_count}</div>
                        <div><strong>Profile Status:</strong> {s.profile_status}</div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Active Syncs Banner ───

function ActiveSyncsBanner({ syncs }: { syncs: ActiveSync[] }) {
  if (syncs.length === 0) return null;

  return (
    <div style={{ backgroundColor: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem' }}>
      <div style={{ fontWeight: 600, fontSize: '0.875rem', color: '#92400e', marginBottom: '0.375rem' }}>
        {syncs.length} sync{syncs.length !== 1 ? 's' : ''} running now
      </div>
      {syncs.map(s => (
        <div key={s.id} style={{ fontSize: '0.8125rem', color: '#78350f', display: 'flex', gap: '1rem', padding: '0.25rem 0' }}>
          <span style={{ fontWeight: 500 }}>{s.profile_name}</span>
          <span>{s.records_synced.toLocaleString()} records</span>
          {s.errors_count > 0 && <span style={{ color: '#ef4444' }}>{s.errors_count} errors</span>}
          <span style={{ color: '#92400e' }}>started {relativeTime(s.started_at)}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Main Page ───

export default function SyncMonitoringPage() {
  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [health, setHealth] = useState<SyncHealth[]>([]);
  const [throughput, setThroughput] = useState<ThroughputBucket[]>([]);
  const [errorsTrend, setErrorsTrend] = useState<ErrorTrendBucket[]>([]);
  const [activeSyncs, setActiveSyncs] = useState<ActiveSync[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const fetchAll = useCallback(async () => {
    try {
      const [ov, sh, tp, et, as_] = await Promise.all([
        api.get<OverviewData>('/monitoring/overview'),
        api.get<{ syncs: SyncHealth[] }>('/monitoring/sync-health'),
        api.get<{ throughput: ThroughputBucket[] }>('/monitoring/throughput'),
        api.get<{ errors_trend: ErrorTrendBucket[] }>('/monitoring/errors-trend'),
        api.get<{ active_syncs: ActiveSync[] }>('/monitoring/active-syncs'),
      ]);
      setOverview(ov);
      setHealth(sh.syncs);
      setThroughput(tp.throughput);
      setErrorsTrend(et.errors_trend);
      setActiveSyncs(as_.active_syncs);
      setLastRefresh(new Date());
    } catch {
      // API errors handled by UI state — overview stays null, showing empty cards
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 30000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <p style={{ color: '#9ca3af' }}>Loading monitoring data...</p>
      </div>
    );
  }

  const errorRateColor = (overview?.error_rate ?? 0) > 15 ? '#ef4444' : (overview?.error_rate ?? 0) > 5 ? '#eab308' : '#22c55e';

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', margin: 0 }}>Sync Monitoring</h1>
          <p style={{ fontSize: '0.8125rem', color: '#9ca3af', margin: '0.25rem 0 0' }}>
            Real-time sync health and performance &middot; Last refresh: {lastRefresh.toLocaleTimeString()}
          </p>
        </div>
        <button
          onClick={fetchAll}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: ACCENT,
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            fontSize: '0.875rem',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          Refresh
        </button>
      </div>

      {/* Active syncs banner */}
      <ActiveSyncsBanner syncs={activeSyncs} />

      {/* Stat cards */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        <StatCard label="Active Syncs" value={overview?.active_syncs ?? 0} color="#22c55e" subtitle={`${overview?.total_runs_today ?? 0} runs today`} />
        <StatCard label="Records Today" value={(overview?.records_today ?? 0).toLocaleString()} color="#3b82f6" />
        <StatCard label="Error Rate" value={`${overview?.error_rate ?? 0}%`} color={errorRateColor} />
        <StatCard label="Avg Duration" value={formatDuration(overview?.avg_duration_ms ?? 0)} color="#6b7280" />
      </div>

      {/* Throughput chart */}
      <div style={{ marginBottom: '1.25rem' }}>
        <ThroughputChart data={throughput} />
      </div>

      {/* Bottom row: health table + error trend */}
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        <SyncHealthTable syncs={health} />
        <ErrorTrendChart data={errorsTrend} />
      </div>
    </div>
  );
}
